import React, {
    useEffect,
    useMemo,
    useCallback,
    useState,
    useRef,
    FC
} from "react"
import { push, replace } from "connected-react-router"
import { useDispatch } from "react-redux"
import { ModalPanel, SkipNavigationLink } from "dash-styled"
import { hasAnnotationFieldSchema } from "resha"
import { AnnotationOutputSchemaItem } from "universal-schema/lib"

import {
    cancelJob,
    enqueueJob,
    relabelJob,
    addTagByJobID,
    removeTagByJobID,
    deleteJob,
    createFeedbackMessage,
    stopJobIdsPoll,
    startJobIdsPoll,
    startJobIdsOfBatchPoll,
    stopJobIdsOfBatchPoll,
    markJobCorrectness
} from "../../../../../../Actions/API/Job/JobActions"
import { markTaskCorrectness } from "../../../../../../Actions/API/Job/TaskActions"
import { reloadTags } from "../../../../../../Actions/API/AppInstance/AppInstanceActions"
import { TrackingType } from "../../../../../../Actions/Types"

import {
    hasValue,
    FetchState,
    getValue,
    hasResult
} from "../../../../../../Models/Client/FetchState"
import {
    APIJobQueryModel,
    APIJobModel,
    JobType,
    JobStatus,
    RelabelJobResponse,
    JobIdsOfBatchResponse,
    JobVersionItem
} from "../../../../../../Models/API/JobModel"
import { FilterAndSortExpression } from "../../../../../../Models/Client/FilterAndSortExpression"
import { findAnnotationOutput } from "../../../../../../Models/Client/AnnotationOutputSchemaItem"
import { SortExpression } from "../../../../../../Models/Client/SortExpression"
import { FilterExpression } from "../../../../../../Models/Client/FilterExpression"
import { DataAccess } from "../../../../../../Models/Client/DataAccess"
import { hasAppPermission } from "../../../../../../Models/Client/ProjectRole"
import { APIJobsPerStatusModel } from "../../../../../../Models/API/APIJobsPerStatusModel"
import { getItemList } from "../../../../../../Models/Client/RemoteData"

import { useAppDetailsPaths } from "../../../../../../Hooks/useAppDetailsPaths"
import { useUrlSigner } from "../../../../../../Hooks/useUrlSigner"
import { useJobHistory } from "../../../../../../Hooks/useJobHistory"
import { useJobVersionResponse } from "../../../../../../Hooks/useJobVersionResponse"
import { useErrorTrackingInfo } from "../../../../../../Hooks/useErrorTrackingInfo"
import { useAppSchema } from "../../../../../../Hooks/useAppSchema"
import { usePrelabels } from "../../../../../../Hooks/usePrelabels"
import { useBoolean } from "../../../../../../Hooks/useBoolean"
import { useJob } from "../../../../../../Hooks/useJob"
import { useJobListNavigation } from "../../../../../../Hooks/useJobListNavigation"
import { useAppTemplate } from "../../../../../../Hooks/useAppTemplate"
import { useJobDownload } from "../../../../../../Hooks/useJobDownload"
import { useAppInstance } from "../../../../../../Hooks/useAppInstance"
import { useAssumedRoles } from "../../../../../../Hooks/useAssumedRoles"
import { useFeedbackMessages } from "../../../../../../Hooks/useFeedbackMessages"
import { useFeedbackTags } from "../../../../../../Hooks/useFeedbackTags"
import { useTaskDetailsPaths } from "../../../../../../Hooks/useTaskDetailsPaths"
import { useAppState } from "../../../../../../Hooks/useReduxState"
import { useTaskDetails } from "../../../../../../Hooks/useTaskDetails"
import { useJobPostProcessAsyncOperation } from "../../../../../../Hooks/useJobPostProcessAsyncOperation"
import { useJobResponse } from "../../../../../../Hooks/useJobResponse"
import { DownloadType } from "../../../../../../Hooks/useJobListDownload"

import { ModalPanelNavHeader } from "../../../../../Common/ModalPanelNavHeader/ModalPanelNavHeader"
import { LoadingContainer } from "../../../../../Common/LoadingContainer/LoadingContainer"
import { FilterFields } from "../../../../../Common/FilterComposer/FilterComposer"
import { NothingMatched } from "../../../../../Common/Empty/NothingMatched"
import { DataPointVersionListItem } from "../../../Common/DataPointVersionListItem/DataPointVersionListItem"
import { DownloadTypeModal } from "../../../Common/Modals/DownloadTypeModal/DownloadTypeModal"

import { MainThunkDispatch } from "../../../../../../Reducers"
import { DataAccessContext } from "../../../../../../Context/DataAccessContext"
import { routePaths } from "../../../../../../Routes"
import { findImageUrlPathForDownload } from "../../../../../../Helpers/Schema/Schema"

import { DeleteModal } from "../Common/DeleteModal"
import { EnqueueModal } from "../Common/EnqueueModel"
import { CancelConfirmModal } from "../Common/CancelConfirmModal"
import { RelabelConfirmModal } from "../Common/RelabelConfirmModal"
import {
    jobSortOptions,
    defaultJobFilterOnAdd,
    jobWithMessagesSortOptions
} from "../Common/constants"
import { DetailViewSidebar } from "./Sidebar/DetailViewSidebar"
import {
    JobDetailView,
    JobDetailViewProps
} from "./Job/JobDetailView_deprecated"
import {
    TaskDetailView,
    TaskDetailViewProps
} from "./Task/TaskDetailView_deprecated"

interface DetailViewProps {
    orgUsername: string
    appID: string
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    selectedJobs?: number[]
    /**
     * Identifier of the job to show.
     * - `undefined` or `"loading"` means the job to show is not decided yet.
     * - `Result.err` means there is nothing to show.
     * - `Result.ok` encapsulates the identifier of the job to show
     */
    jobIDFetch: FetchState<string>
    versionFetch: FetchState<string>
    taskIDFetch: FetchState<string>
    clearFilterOnCloseModal: boolean
    navigationItems?: APIJobModel[]
    filterFields: FilterFields<APIJobQueryModel & { output: unknown }>
    onClearSelection: () => void
}

type Dialog =
    | "relabel"
    | "cancel"
    | "delete"
    | "enqueue"
    | "download"
    | undefined

export const DetailView: FC<DetailViewProps> = ({
    orgUsername,
    appID,
    jobIDFetch,
    versionFetch,
    taskIDFetch,
    filterAndSort,
    clearFilterOnCloseModal,
    navigationItems,
    filterFields,
    onClearSelection,
    selectedJobs
}) => {
    const jobID = getValue(jobIDFetch) ?? undefined
    const version = getValue(versionFetch) ?? undefined
    const taskID = getValue(taskIDFetch) ?? undefined

    useErrorTrackingInfo("superai.jobID", jobID)

    const dispatch = useDispatch<MainThunkDispatch>()
    const rolesFetch = useAssumedRoles(appID)
    const templateFetch = useAppTemplate(appID)
    const instanceFetch = useAppInstance(appID)
    const schemaFetch = useAppSchema(appID, false)
    const jobFetch = useJob(appID, jobID)
    const appFeedbackTags = useFeedbackTags(appID)
    const outputSchema = hasValue(schemaFetch)
        ? schemaFetch.value.output
        : undefined

    const annotationItem = useMemo(
        () =>
            outputSchema
                ? findAnnotationOutput(
                      outputSchema,
                      JobStatus.COMPLETED,
                      undefined
                  )
                : undefined,
        [outputSchema]
    )
    const isLabelByCollaborator =
        hasValue(jobFetch) && jobFetch.value.jobType === JobType.COLLABORATOR

    const prelabels = usePrelabels(appID, jobID)

    const [selectedVersion, setVersion] = useState<number>(0)
    const [isMessagesPath, appDetailPath] = useAppDetailsPaths()
    // dispatch event when job viewed
    useEffect(() => {
        if (!hasValue(jobFetch)) return

        dispatch({
            type: TrackingType.job_view,
            payload: jobFetch.value
        })
    }, [jobFetch, dispatch])

    useEffect(() => {
        if (version !== undefined) {
            setVersion(parseInt(version))
        }
    }, [version])

    const [isJobRelabeled, setIsRelabeledTrue, setIsRelabeledFalse] =
        useBoolean(false)

    const isJobRelabeledRef = useRef(false)

    useEffect(() => {
        isJobRelabeledRef.current = isJobRelabeled
    }, [isJobRelabeled])

    const relabelJobCleanup = useCallback(() => {
        isJobRelabeledRef.current = false
        setIsRelabeledFalse()
        dispatch(stopJobIdsPoll())
        dispatch(stopJobIdsOfBatchPoll())
    }, [dispatch, setIsRelabeledFalse])

    const isPostProcessAvailable =
        getValue(templateFetch)?.metadata.post_process_data === "available"

    const postProcessOperation = useJobPostProcessAsyncOperation(
        appID,
        getValue(jobFetch)?.uuid
    )
    const jobResponseFetch = useJobResponse(
        appID,
        jobID,
        isPostProcessAvailable,
        postProcessOperation
    )
    const jobHistoryFetch = useJobHistory(jobID)
    const jobVersionFetch = useJobVersionResponse(appID, jobID, selectedVersion)

    const jobVersion = getValue(jobVersionFetch)
    const jobFeedbackMessages = useFeedbackMessages(
        jobID,
        taskID,
        isLabelByCollaborator
    )
    const navigation = useJobListNavigation(
        orgUsername,
        appID,
        { ...filterAndSort, clearFilterOnCloseModal },
        jobID,
        navigationItems,
        isMessagesPath
    )
    const [isDownloading, setDownloading] = useState(false)
    const [dialog, setDialog] = useState<Dialog>(undefined)
    const hideDialog = useCallback(() => {
        setDialog(undefined)
        setDownloading(false)
    }, [])
    const confirmRelabel = useCallback(() => setDialog("relabel"), [])
    const confirmCancel = useCallback(() => setDialog("cancel"), [])
    const confirmDelete = useCallback(() => setDialog("delete"), [])
    const confirmEnqueue = useCallback(() => setDialog("enqueue"), [])
    const [downloadType, setDownloadType] = useState<DownloadType>("json")

    const handleNavPrev = useCallback(() => {
        if (navigation?.onPrev) {
            setVersion(0)
            navigation?.onPrev()
        }
    }, [navigation])
    const handleNavNext = useCallback(() => {
        if (navigation?.onNext) {
            setVersion(0)
            navigation?.onNext()
        }
    }, [navigation])
    const handleClose = useCallback(
        () =>
            dispatch(
                push(
                    appDetailPath(
                        orgUsername,
                        appID,
                        clearFilterOnCloseModal
                            ? { ...filterAndSort, filter: [] }
                            : filterAndSort,
                        undefined,
                        jobID
                    )
                )
            ),
        [
            appDetailPath,
            appID,
            jobID,
            clearFilterOnCloseModal,
            dispatch,
            filterAndSort,
            orgUsername
        ]
    )

    const handleRelabeledJobIdFetch = useCallback(
        (jobData: APIJobModel) => {
            if (jobData !== undefined && isJobRelabeledRef.current) {
                dispatch(
                    replace(
                        routePaths.jobsDetail(
                            orgUsername,
                            jobData.appId,
                            jobData.id,
                            filterAndSort
                        )
                    )
                )
            }
        },
        [dispatch, filterAndSort, orgUsername]
    )

    const handleReview = useCallback(
        async (isCorrect: boolean) => {
            if (jobID !== undefined) {
                await dispatch(markJobCorrectness(appID, jobID, isCorrect))
            }
        },
        [appID, jobID, dispatch]
    )

    const handleRelabel = useMemo(() => {
        if (jobID === undefined) return undefined
        return () => {
            setIsRelabeledTrue()
            setVersion(0)
            return dispatch(relabelJob(jobID, false))
                .then((relabelResponse: RelabelJobResponse | undefined) => {
                    if (relabelResponse) {
                        dispatch(
                            startJobIdsOfBatchPoll(
                                appID,
                                relabelResponse.batch_id
                            )
                        ).then((jobIdsResponse: JobIdsOfBatchResponse) => {
                            dispatch(stopJobIdsOfBatchPoll())
                            const newJobUUID = jobIdsResponse.job_ids[0]
                            if (newJobUUID !== undefined) {
                                dispatch(startJobIdsPoll(newJobUUID)).then(
                                    (payload: APIJobModel) => {
                                        handleRelabeledJobIdFetch(payload)
                                        setIsRelabeledFalse()
                                    }
                                )
                            }
                        })
                    }
                })
                .finally(hideDialog)
        }
    }, [
        appID,
        dispatch,
        handleRelabeledJobIdFetch,
        hideDialog,
        jobID,
        setIsRelabeledFalse,
        setIsRelabeledTrue
    ])
    const handleCancel = useMemo(
        () =>
            jobID === undefined
                ? undefined
                : () =>
                      dispatch(cancelJob(appID, jobID, false)).finally(
                          hideDialog
                      ),
        [hideDialog, dispatch, appID, jobID]
    )
    const handleDelete = useMemo(
        () =>
            jobID === undefined
                ? undefined
                : () =>
                      dispatch(deleteJob(appID, jobID, false)).finally(() => {
                          if (navigation?.onNext) navigation?.onNext()
                          else if (navigation?.onPrev) navigation?.onPrev()
                          else handleClose()
                          hideDialog()
                      }),
        [hideDialog, dispatch, appID, jobID, handleClose, navigation]
    )
    const handleEnqueue = useMemo(
        () =>
            jobID === undefined
                ? undefined
                : () =>
                      dispatch(enqueueJob(appID, jobID, false)).finally(
                          hideDialog
                      ),
        [hideDialog, dispatch, jobID, appID]
    )
    const handleChangeSort = (sort: SortExpression<APIJobQueryModel>) =>
        dispatch(
            push(
                appDetailPath(orgUsername, appID, {
                    ...filterAndSort,
                    openDetail: true,
                    sort
                })
            )
        )
    const handleChangeFilter = (filter: FilterExpression<APIJobQueryModel>[]) =>
        dispatch(
            push(
                appDetailPath(orgUsername, appID, {
                    ...filterAndSort,
                    openDetail: true,
                    filter
                })
            )
        )
    const handleAddTag = useMemo(
        () =>
            jobID !== undefined &&
            hasValue(jobFetch) &&
            hasAppPermission(getValue(rolesFetch) ?? [], "job:edit-tags")
                ? async (tag: string, isNew: boolean) => {
                      await dispatch(addTagByJobID(appID, jobID, tag))

                      if (isNew) {
                          dispatch(reloadTags(appID))
                      }
                  }
                : undefined,
        [appID, jobID, dispatch, jobFetch, rolesFetch]
    )
    const handleRemoveTag = useMemo(
        () =>
            jobID !== undefined &&
            hasValue(jobFetch) &&
            hasAppPermission(getValue(rolesFetch) ?? [], "job:edit-tags")
                ? (tag: string) => dispatch(removeTagByJobID(appID, jobID, tag))
                : undefined,
        [appID, jobID, dispatch, jobFetch, rolesFetch]
    )

    useEffect(() => {
        relabelJobCleanup()

        return () => {
            relabelJobCleanup()
        }
    }, [jobID, relabelJobCleanup])

    const sortOptions = {
        applied: filterAndSort.sort,
        fields: isMessagesPath ? jobWithMessagesSortOptions : jobSortOptions,
        onChange: handleChangeSort
    }
    const filterOptions = {
        applied: filterAndSort.filter,
        fields: filterFields,
        defaultOnAdd: defaultJobFilterOnAdd,
        onChange: handleChangeFilter
    }

    const onSendFeedbackMessage = useCallback(
        async (message: string) => {
            if (jobID) {
                return dispatch(
                    createFeedbackMessage(
                        jobID,
                        message,
                        taskID,
                        isLabelByCollaborator
                    )
                )
            }
        },
        [dispatch, isLabelByCollaborator, jobID, taskID]
    )

    const versionsList = useMemo(() => {
        if (!hasValue(jobHistoryFetch)) {
            return undefined
        }

        const jobHistory = jobHistoryFetch.value
        const indexOffset = isJobRelabeled ? 1 : 0
        const totalVersions =
            (jobHistory !== undefined ? jobHistory.total + 1 : 1) + indexOffset
        const job = getValue(jobFetch)
        const jobResponse = getValue(jobResponseFetch)

        let payload: JobVersionItem[] = []

        if (job !== undefined) {
            const oldVersions =
                jobHistory === undefined
                    ? []
                    : jobHistory.history.map((versionItem, index) => {
                          const versionName = `Version ${versionItem.version}`
                          const creationDate = versionItem.jobCreatedEdited
                              ? new Date(versionItem.jobCreatedEdited)
                              : null
                          const jobResponse =
                              index === selectedVersion - 1 &&
                              jobVersion !== undefined
                                  ? {
                                        response: jobVersion.response,
                                        responseData: jobVersion.responseData
                                    }
                                  : {
                                        response: undefined,
                                        responseData: undefined
                                    }

                          return {
                              jobID: job.id,
                              versionJobId: `${versionItem.versionJobId}`,
                              originalJobId: `${versionItem.jobId}`,
                              value: index + indexOffset + 1,
                              versionName,
                              correct: versionItem.correct,
                              status: versionItem.status,
                              created: creationDate,
                              isLatestVersion: false,
                              ...jobResponse,
                              label: (
                                  <DataPointVersionListItem
                                      created={creationDate}
                                      correct={versionItem.correct}
                                      status={versionItem.status}
                                      version={versionName}
                                      jobType={job.jobType}
                                  />
                              )
                          }
                      })

            const relabeledVersion = {
                jobID: "",
                versionJobId: "",
                originalJobId: "",
                value: 0,
                versionName: "Latest Version",
                correct: false as null | boolean,
                status: JobStatus.IN_CREATION,
                created: new Date() as Date | null,
                isLatestVersion: false,
                response: null as unknown,
                responseData: null as unknown,
                label: (
                    <DataPointVersionListItem
                        created={new Date()}
                        correct={false}
                        status={JobStatus.IN_CREATION}
                        version={"Latest Version"}
                        jobType={job.jobType}
                    />
                )
            }

            const currentJobCreationDate =
                jobHistory !== undefined &&
                jobHistory.history.length &&
                jobHistory.history[0].created !== null
                    ? new Date(jobHistory.history[0].created)
                    : job.created === ""
                    ? null
                    : new Date(job.created)

            const currentJobVersionName = isJobRelabeled
                ? `Version ${totalVersions - 1}`
                : "Latest Version"

            const latestVersion = {
                jobID: job.id,
                versionJobId: job.id,
                originalJobId: job.id,
                value: 0 + indexOffset,
                versionName: currentJobVersionName,
                correct: job.correct,
                status: job.status,
                created: currentJobCreationDate,
                isLatestVersion: true,
                response: jobResponse?.response,
                responseData: jobResponse?.responseData,
                label: (
                    <DataPointVersionListItem
                        created={currentJobCreationDate}
                        correct={job.correct}
                        status={job.status}
                        version={currentJobVersionName}
                        jobType={job.jobType}
                    />
                )
            }

            payload = [latestVersion].concat(oldVersions)

            if (isJobRelabeled) {
                payload = [relabeledVersion].concat(payload)
            }
        }

        return payload
    }, [
        jobHistoryFetch,
        isJobRelabeled,
        jobFetch,
        jobResponseFetch,
        selectedVersion,
        jobVersion
    ])

    const dataAccess = useMemo(() => {
        if (jobID === undefined || versionsList === undefined) {
            return DataAccess.owner
        }

        const originalJobIdOfSelectedVersion =
            versionsList[selectedVersion]?.originalJobId

        if (originalJobIdOfSelectedVersion === undefined) {
            return DataAccess.perJob(jobID)
        }

        return DataAccess.perJob(originalJobIdOfSelectedVersion)
    }, [jobID, selectedVersion, versionsList])
    const urlSigner = useUrlSigner(dataAccess)

    const imageDownloadPath = useMemo(
        () =>
            findImageUrlPathForDownload(getValue(templateFetch)?.outputSchema),
        [templateFetch]
    )

    const allowResponseDataDownload = useMemo(
        () => imageDownloadPath !== undefined || isPostProcessAvailable,
        [imageDownloadPath, isPostProcessAvailable]
    )

    const onDownload = useJobDownload(
        urlSigner,
        appID,
        isJobRelabeled,
        selectedVersion,
        jobID,
        downloadType === "response_data" ? imageDownloadPath : undefined,
        downloadType === "response_data" && isPostProcessAvailable
    )

    const confirmDownload = useCallback(async () => {
        allowResponseDataDownload ? setDialog("download") : onDownload?.()
    }, [allowResponseDataDownload, onDownload])

    const handleDownload = useCallback(async () => {
        setDownloading(true)
        try {
            await onDownload?.()
        } finally {
            hideDialog()
        }
    }, [onDownload, hideDialog])
    const [, taskDetailPath] = useTaskDetailsPaths()
    const handleTaskOnChange = useCallback(
        (newTaskID: string) => {
            if (versionsList === undefined) {
                return
            }

            dispatch(
                push(
                    taskDetailPath(
                        orgUsername,
                        appID,
                        versionsList[selectedVersion].jobID,
                        String(selectedVersion),
                        newTaskID,
                        filterAndSort
                    )
                )
            )
        },
        [
            appID,
            dispatch,
            filterAndSort,
            orgUsername,
            selectedVersion,
            taskDetailPath,
            versionsList
        ]
    )

    const [wasTaskListToggled, , , , setWasTaskListToggled] = useBoolean(false)
    const [taskListScrollPosition, setTaskListScrollPosition] = useState(0)
    const currentJob = versionsList?.[selectedVersion]

    const taskListData = useAppState(state => {
        if (
            hasValue(jobFetch) &&
            jobFetch.value.id !== undefined &&
            currentJob !== undefined
        ) {
            return state.api.tasks.table[currentJob.versionJobId]
        } else {
            return
        }
    })
    const taskList = useMemo(
        () => (taskListData !== undefined ? getItemList(taskListData) : []),
        [taskListData]
    )

    const selectedTaskDetails = useTaskDetails(currentJob?.versionJobId, taskID)
    const handleTaskReview = useCallback(
        async (isCorrect: boolean) => {
            if (currentJob !== undefined && taskID !== undefined) {
                await dispatch(
                    markTaskCorrectness(
                        currentJob.versionJobId,
                        taskID,
                        isCorrect
                    )
                )
            }
        },
        [taskID, currentJob, dispatch]
    )

    const renderBody = () => {
        if (hasResult(jobIDFetch) && jobIDFetch.isError) {
            return (
                <NothingMatched
                    anchor="maincontent"
                    messageVariant="maybe-try"
                    noFilterLink={appDetailPath(orgUsername, appID, {
                        openDetail: true,
                        filter: [],
                        sort: filterAndSort.sort,
                        clearFilterOnCloseModal
                    })}
                />
            )
        }

        if (
            versionsList === undefined ||
            !hasValue(jobIDFetch) ||
            !hasValue(jobFetch) ||
            !hasValue(templateFetch) ||
            !hasValue(instanceFetch) ||
            !hasValue(schemaFetch) ||
            !hasValue(rolesFetch) ||
            (jobFetch.value.status === JobStatus.COMPLETED &&
                !hasValue(jobResponseFetch))
        ) {
            return undefined
        }

        const props: Partial<JobDetailViewProps> &
            Omit<TaskDetailViewProps, "taskID"> = {
            orgUsername,
            appID,
            job: jobFetch.value,
            instance: instanceFetch.value,
            versionsList,
            selectedVersion,
            filterAndSort,
            prelabels,
            showOldVersion: versionsList.length > 1 && selectedVersion > 0,
            taskList,
            onChangeSelectedVersion: setVersion,
            onTaskChange: handleTaskOnChange
        }

        const jobDetailViewProps: JobDetailViewProps = {
            ...props,
            edit: templateFetch.value.metadata.edit_output ?? "labeling",
            postProcessedData:
                templateFetch.value.metadata.post_process_data ??
                "not_available",
            postProcessTitle: templateFetch.value.metadata.post_process_title,
            clearFilterOnCloseModal,
            isMessagesPath,
            isJobRelabeled,
            wasTaskListToggled,
            taskListScrollPosition,
            setWasTaskListToggled,
            role: getValue(rolesFetch) ?? [],
            onDownload: confirmDownload,
            onRelabelRequest: confirmRelabel,
            onCancel: confirmCancel,
            onDelete: confirmDelete,
            saveScrollPosition: setTaskListScrollPosition,
            onEnqueue: handleEnqueue,
            onRelabel: handleRelabel,
            handleCancel,
            urlSigner,
            inputSchema: schemaFetch.value.input,
            outputSchema: schemaFetch.value.output,
            onAddTag: handleAddTag,
            onRemoveTag: handleRemoveTag
        }

        return (
            <>
                <div
                    id="maincontent"
                    className={`w-0 flex-shrink flex-grow border-t-0 border-b-0 border-l-0 border-solid border-gray-300`}
                >
                    <div
                        className="p-4 pb-0 h-full flex flex-col"
                        data-testid="detail-view-body"
                    >
                        {taskID === undefined ? (
                            <JobDetailView {...jobDetailViewProps} />
                        ) : (
                            <TaskDetailView
                                {...props}
                                className="h-full flex-grow flex-shrink"
                                taskID={taskID}
                            />
                        )}
                    </div>
                </div>
                {!hasAnnotationFieldSchema(
                    // render sidebar only in old schema apps
                    (annotationItem as AnnotationOutputSchemaItem) || {}
                ) && (
                    <DetailViewSidebar
                        orgUsername={orgUsername}
                        roles={rolesFetch.value}
                        appID={appID}
                        instance={instanceFetch.value}
                        jobID={jobIDFetch.value}
                        filterAndSort={filterAndSort}
                        job={jobFetch.value}
                        onCorrectnessChange={
                            taskID === undefined
                                ? handleReview
                                : handleTaskReview
                        }
                        onEnqueue={confirmEnqueue}
                        selectedJobs={selectedJobs}
                        feedbackMessages={getValue(jobFeedbackMessages)}
                        onSendFeedbackMessage={onSendFeedbackMessage}
                        feedbackTags={getValue(appFeedbackTags)}
                        isMessagesPath={isMessagesPath}
                        canVerify={
                            (taskID === undefined ||
                                (taskID !== undefined &&
                                    hasValue(selectedTaskDetails))) &&
                            ((selectedVersion === 0 && !isJobRelabeled) ||
                                (selectedVersion === 1 && isJobRelabeled))
                        }
                        taskList={taskList}
                        selectedTask={getValue(selectedTaskDetails)}
                    />
                )}
            </>
        )
    }

    const jobsByStatus = useMemo(
        () =>
            hasValue(jobFetch)
                ? {
                      ...APIJobsPerStatusModel.zeros(),
                      [jobFetch.value.status]: 1
                  }
                : undefined,
        [jobFetch]
    )

    return (
        <DataAccessContext.Provider value={dataAccess}>
            <SkipNavigationLink anchor="maincontent" />
            <ModalPanel
                onClose={handleClose}
                testId="review-modal"
                noPadding={true}
                scrimOpacity="darker"
                className="mt-16 w-full relative min-h-full md:min-h-0 text-sm md:flex-1 md:rounded-b-none"
                position="top"
                disableGutters={true}
                panelColumn={true}
                hideClose={true}
            >
                <ModalPanelNavHeader
                    mode={navigationItems === undefined ? "all" : "selected"}
                    sort={sortOptions}
                    filter={filterOptions}
                    cursor={navigation?.cursor}
                    onPrev={navigation?.onPrev ? handleNavPrev : undefined}
                    onNext={navigation?.onNext ? handleNavNext : undefined}
                    onClearSelection={onClearSelection}
                    containerClassName="-mt-16"
                    onClose={handleClose}
                    disableSortAndFilter={isJobRelabeled}
                />
                <LoadingContainer
                    defaultSize={{ height: 480 }}
                    className="flex w-full h-full overflow-hidden"
                >
                    {renderBody()}
                </LoadingContainer>
            </ModalPanel>
            {handleRelabel !== undefined && dialog === "relabel" ? (
                <RelabelConfirmModal
                    total={1}
                    onYes={handleRelabel}
                    onNo={hideDialog}
                    jobsByStatus={jobsByStatus}
                    isLabelByCollaborator={isLabelByCollaborator}
                />
            ) : handleCancel !== undefined && dialog === "cancel" ? (
                <CancelConfirmModal
                    total={1}
                    onYes={handleCancel}
                    onNo={hideDialog}
                    jobsByStatus={jobsByStatus}
                    isLabelByCollaborator={isLabelByCollaborator}
                />
            ) : handleDelete !== undefined && dialog === "delete" ? (
                <DeleteModal total={1} onYes={handleDelete} onNo={hideDialog} />
            ) : handleEnqueue !== undefined && dialog === "enqueue" ? (
                <EnqueueModal
                    onNo={hideDialog}
                    onYes={handleEnqueue}
                    total={1}
                    jobsByStatus={jobsByStatus}
                    isLabelByCollaborator={isLabelByCollaborator}
                />
            ) : null}
            {dialog === "download" ? (
                <DownloadTypeModal
                    name={{ singular: "data point", plural: "data points" }}
                    itemCount={1}
                    onClose={hideDialog}
                    onConfirm={isDownloading ? undefined : handleDownload}
                    onSetType={setDownloadType}
                    hasFlatten={false}
                    type={downloadType}
                    willDownload={true}
                    allowCsvDownload={false}
                    allowResponseDataDownload={allowResponseDataDownload}
                />
            ) : null}
        </DataAccessContext.Provider>
    )
}
